import { put, select, takeLeading } from 'redux-saga/effects';
import {
    genericBladeFetchFulfilled, updateLoanPurpose, UpdateLoanPurposeRequest, updateSelectedLoanPurpose,
} from '@commblades/commblades';

import { PayloadAction } from '@reduxjs/toolkit';

import { removeExistingBladesAfter, startBladeNavigation } from '../../navigationSlice';
import Blades from '../Blades';
import {
    BLADE_NAME, LOAN_PURPOSE_ID, RootModel,
} from '../../types';
import { selectRootModel } from '../../../netBank/netBankSelectors';
import { SubLoanPurposeContent, SubLoanPurposeRaw } from '../subLoanPurpose/subLoanPurposeState';
import { getYourFinanceFulfilled } from '../yourFinance/yourFinanceSlice';
import { DefaultYourFinanceSubmit, YourFinanceData } from '../yourFinance/yourFinanceTypes';
import { updateSelectedLoanProductName, updateSelectedLoanPurposeId, updateSelectedSubLoanPurposeId } from '../../../netBank/netBankSlice';
import updateHiddenField from '../../../../utils/trackingUtils';

function* loanPurposesBladeSubmitWorkSaga(action: PayloadAction<UpdateLoanPurposeRequest>) {
    const rootModel: RootModel = yield select(selectRootModel);

    const { loanPurposes, subLoanPurposes, borrowingInput } = rootModel;
    const { selectedLoanPurposeIds } = action.payload;

    if (selectedLoanPurposeIds.length > 0) {
        // Set loan product name
        const matchedLoanPurpose = loanPurposes.data.find(({ id }) => id === selectedLoanPurposeIds[0]);

        if (matchedLoanPurpose && matchedLoanPurpose?.name) {
            updateHiddenField('SC_LOAN_PURPOSE', matchedLoanPurpose.name.toLowerCase());

            if (matchedLoanPurpose.name.toLowerCase().includes('other')) {
                yield put(updateSelectedLoanProductName('purchases'));
            } else if (matchedLoanPurpose.name.toLowerCase().includes('medical')) {
                yield put(updateSelectedLoanProductName('medical & dental expenses'));
            } else {
                yield put(updateSelectedLoanProductName(matchedLoanPurpose.name.toLowerCase()));
            }
        }

        const matchedSubLoanPurpose: Array<SubLoanPurposeRaw> = (subLoanPurposes as Array<SubLoanPurposeRaw>)
            .filter(({ id }: SubLoanPurposeRaw) => id === (selectedLoanPurposeIds)[0]);

        // set new selected loan purpose id and reset sub loan purpose id
        yield put(updateSelectedLoanPurposeId(selectedLoanPurposeIds[0]));
        yield put(updateSelectedSubLoanPurposeId(LOAN_PURPOSE_ID.INVALID));

        if (matchedSubLoanPurpose && matchedSubLoanPurpose[0]) {
            const subLoanPurpose = matchedSubLoanPurpose[0];

            if (!subLoanPurpose.content) {
                const yourFinance: YourFinanceData = JSON.parse(borrowingInput.content) as YourFinanceData;

                yield put(getYourFinanceFulfilled({
                    data: yourFinance,
                    submitData: DefaultYourFinanceSubmit,
                    isSubmitDataReady: false,
                }));
                yield put(startBladeNavigation(BLADE_NAME.BORROWING_INPUT));
            } else if (subLoanPurpose.id === LOAN_PURPOSE_ID.DEBT_CONSOLIDATION) {
                const debtConsolidationData: YourFinanceData = JSON.parse(subLoanPurpose.content);

                yield put(getYourFinanceFulfilled({
                    data: debtConsolidationData,
                    submitData: DefaultYourFinanceSubmit,
                    isSubmitDataReady: false,
                }));
                yield put(startBladeNavigation(BLADE_NAME.DEBT_CONSOLIDATION));
            } else {
                const { content, data } = subLoanPurpose;
                const contentObj: SubLoanPurposeContent = JSON.parse(content);

                contentObj.form1 = {
                    label: contentObj.dropDownLabelText,
                };
                contentObj.validations.hardValidations = {
                    form1: {
                        defaultErrorMessage: contentObj.validations.errorMessage,
                    },
                };

                if (data) {
                    contentObj.form1.dropdownOptions = data.map(({ id, description }) => ({
                        value: id.toString(),
                        key: description,
                    }));
                }

                yield put(genericBladeFetchFulfilled({
                    id: 'sub-loanpurpose',
                    fetchPayload: { content: JSON.stringify(contentObj) },
                }));

                yield put(startBladeNavigation(Blades.LoanPurposes.next));
            }
        }
    }
}

function* removeFollowBladesWorkSaga(action: PayloadAction<UpdateLoanPurposeRequest>) {
    yield put(removeExistingBladesAfter('LoanPurposes'));
}

export function* loanPurposesBladeSubmitWatchSaga() {
    yield takeLeading(updateLoanPurpose.type, loanPurposesBladeSubmitWorkSaga);
    yield takeLeading(updateSelectedLoanPurpose.type, removeFollowBladesWorkSaga);
}
